import { createStore, type StoreApi } from 'zustand/vanilla';
import type { BoxAnchor, ConnectorProps, Geometry } from '../doc/schema';
import type { Rect, Size } from '../doc/geometry';
import type { Camera } from './viewport/camera';
import type { SnapGuide } from './selection/snapping';

export type Tool =
  | 'select'
  | 'pan'
  | 'note'
  | 'title'
  | 'richtext'
  | 'table'
  | 'todo'
  | 'kanban'
  | 'shape'
  | 'connector'
  | 'frame'
  | 'draw'
  | 'cutout'
  | 'sheet'
  | 'exportArea';

/** How a stroke looks while the eraser is still working on it (see eraser.ts). */
export interface InkPreview {
  /** A line: the pieces left so far, each flat [x, y, pressure, ...] in the stroke's own space, with its cut flags. */
  runs?: { points: number[]; cut: number }[];
  /** A fill: its erased paths so far (in place of the stored ones). */
  erased?: number[][];
}

/** Where a connector end would attach: an item, at one of its side dots or (anchor null) floating. */
export interface ConnectTarget {
  itemId: string;
  anchor: BoxAnchor | null;
}

/**
 * Per-pane UI state. Everything that is *not* document content: camera, selection, tool,
 * in-progress drag geometry. A second pane (split view) simply gets a second store.
 */
export interface PaneState {
  camera: Camera;
  /** Zoom after the user stopped zooming; image LOD switches on this, not on every frame. */
  settledZoom: number;
  size: Size;
  tool: Tool;
  selection: ReadonlySet<string>;
  editingId: string | null;
  /** Geometry overrides while dragging/resizing, committed to the doc on release. */
  transient: Readonly<Record<string, Partial<Geometry>>>;
  /** Strokes the eraser is working on, drawn from here until it lets go. */
  inkPreview: Readonly<Record<string, InkPreview>>;
  /** Connector props being edited with their handles, committed to the doc on release. */
  connectorDraft: Readonly<Record<string, Partial<ConnectorProps>>>;
  /** The connector being drawn with the connector tool (not in the document yet). */
  connecting: ConnectorProps | null;
  /** The item an end would attach to (highlighted with its side dots), while drawing or dragging an end. */
  connectTarget: ConnectTarget | null;
  marquee: Rect | null;
  /** Rectangle being dragged out with a creation tool (shape). */
  creating: Rect | null;
  /** Snap guides (world coords) shown during a drag: segments joining the moved item and the one it met. */
  snapGuides: { x: SnapGuide | null; y: SnapGuide | null };
  spaceHeld: boolean;
  dragOver: boolean;
  /** Sketch sheet being painted (the sheet tool in paint mode), and its active layer. */
  paintSheetId: string | null;
  paintLayerId: string | null;
  /** The interface (toolbar, panels, view bar, status bar) is hidden: only the canvas shows. */
  uiHidden: boolean;
  /** The cutout drawer is open along the pane's edge (see drawer/CutoutDrawer.tsx). */
  drawerOpen: boolean;
  /** Shift+Q: the drawer, once it is up, saves the selection and clears this. */
  drawerSaveRequested: boolean;
  /** The frame index is open beside the canvas (see views/FrameIndex.tsx). */
  framesOpen: boolean;
  /** The board CSS editor is open (css/CssEditor.tsx). */
  cssEditorOpen: boolean;
  /** Unsaved board CSS being previewed in this pane only; null shows the saved CSS. */
  cssDraft: string | null;

  setCamera(c: Camera): void;
  setSize(s: Size): void;
  setTool(t: Tool): void;
  select(ids: Iterable<string>): void;
  addToSelection(ids: Iterable<string>): void;
  toggleSelected(id: string): void;
  clearSelection(): void;
  setEditing(id: string | null): void;
  setTransient(t: Record<string, Partial<Geometry>>): void;
  clearTransient(): void;
  setInkPreview(p: Record<string, InkPreview> | null): void;
  setConnectorDraft(id: string, draft: Partial<ConnectorProps> | null): void;
  setConnecting(p: ConnectorProps | null): void;
  setConnectTarget(t: ConnectTarget | null): void;
  setMarquee(r: Rect | null): void;
  setCreating(r: Rect | null): void;
  setSnapGuides(g: { x: SnapGuide | null; y: SnapGuide | null }): void;
  setSpaceHeld(v: boolean): void;
  setDragOver(v: boolean): void;
  /** Enters paint mode on a sheet (tool becomes 'sheet'); null leaves it (tool stays 'sheet' for placing). */
  setPaint(sheetId: string | null, layerId?: string | null): void;
  setPaintLayer(layerId: string | null): void;
  setUiHidden(v: boolean): void;
  setDrawerOpen(v: boolean): void;
  /** Opens the drawer and has it save the current selection as a cutout. */
  requestDrawerSave(v?: boolean): void;
  setFramesOpen(v: boolean): void;
  setCssEditorOpen(v: boolean): void;
  setCssDraft(css: string | null): void;
}

export type PaneStore = StoreApi<PaneState>;

const EMPTY_SELECTION: ReadonlySet<string> = new Set();
const EMPTY_TRANSIENT: Readonly<Record<string, Partial<Geometry>>> = Object.freeze({});
const NO_PREVIEW: Readonly<Record<string, InkPreview>> = Object.freeze({});
const NO_DRAFTS: Readonly<Record<string, Partial<ConnectorProps>>> = Object.freeze({});
const NO_GUIDES = Object.freeze({ x: null, y: null }) as { x: SnapGuide | null; y: SnapGuide | null };
const sameGuide = (a: SnapGuide | null, b: SnapGuide | null) => a === b || (!!a && !!b && a.at === b.at && a.from === b.from && a.to === b.to);

export function createPaneStore(initialCamera: Camera = { x: 0, y: 0, zoom: 1, rot: 0 }): PaneStore {
  let settleTimer: ReturnType<typeof setTimeout> | undefined;

  return createStore<PaneState>((set, get) => ({
    camera: initialCamera,
    settledZoom: initialCamera.zoom,
    size: { w: 0, h: 0 },
    tool: 'select',
    selection: EMPTY_SELECTION,
    editingId: null,
    transient: EMPTY_TRANSIENT,
    inkPreview: NO_PREVIEW,
    connectorDraft: NO_DRAFTS,
    connecting: null,
    connectTarget: null,
    marquee: null,
    creating: null,
    snapGuides: NO_GUIDES,
    spaceHeld: false,
    dragOver: false,
    paintSheetId: null,
    paintLayerId: null,
    uiHidden: false,
    drawerOpen: false,
    drawerSaveRequested: false,
    framesOpen: false,
    cssEditorOpen: false,
    cssDraft: null,

    setCamera(camera) {
      const prev = get().camera;
      set({ camera });
      if (camera.zoom !== prev.zoom) {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(() => set({ settledZoom: get().camera.zoom }), 150);
      }
    },
    setSize(size) {
      const s = get().size;
      if (s.w !== size.w || s.h !== size.h) set({ size });
    },
    setTool(tool) {
      if (get().tool === tool) return;
      // any other tool ends paint mode
      const reset = { creating: null, connecting: null, connectTarget: null };
      set(tool === 'sheet' ? { tool, ...reset } : { tool, ...reset, paintSheetId: null, paintLayerId: null });
    },
    select(ids) {
      const next = new Set(ids);
      const cur = get().selection;
      if (next.size === cur.size && [...next].every((id) => cur.has(id))) return;
      set({ selection: next.size ? next : EMPTY_SELECTION });
    },
    addToSelection(ids) {
      const next = new Set(get().selection);
      for (const id of ids) next.add(id);
      set({ selection: next });
    },
    toggleSelected(id) {
      const next = new Set(get().selection);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      set({ selection: next.size ? next : EMPTY_SELECTION });
    },
    clearSelection() {
      if (get().selection.size) set({ selection: EMPTY_SELECTION });
    },
    setEditing(editingId) {
      if (get().editingId !== editingId) set({ editingId });
    },
    setTransient(transient) {
      set({ transient });
    },
    clearTransient() {
      if (get().transient !== EMPTY_TRANSIENT) set({ transient: EMPTY_TRANSIENT });
    },
    setInkPreview(p) {
      const inkPreview = p ?? NO_PREVIEW;
      if (get().inkPreview !== inkPreview) set({ inkPreview });
    },
    setConnectorDraft(id, draft) {
      const cur = get().connectorDraft;
      if (!draft) {
        if (!(id in cur)) return;
        const { [id]: _gone, ...rest } = cur;
        void _gone;
        set({ connectorDraft: Object.keys(rest).length ? rest : NO_DRAFTS });
        return;
      }
      set({ connectorDraft: { ...cur, [id]: draft } });
    },
    setConnecting(connecting) {
      if (get().connecting !== connecting) set({ connecting });
    },
    setConnectTarget(t) {
      const cur = get().connectTarget;
      const same = cur === t || (!!cur && !!t && cur.itemId === t.itemId && cur.anchor?.x === t.anchor?.x && cur.anchor?.y === t.anchor?.y);
      if (!same) set({ connectTarget: t });
    },
    setMarquee(marquee) {
      set({ marquee });
    },
    setCreating(creating) {
      set({ creating });
    },
    setSnapGuides(g) {
      const cur = get().snapGuides;
      if (sameGuide(cur.x, g.x) && sameGuide(cur.y, g.y)) return;
      set({ snapGuides: g.x === null && g.y === null ? NO_GUIDES : g });
    },
    setSpaceHeld(spaceHeld) {
      if (get().spaceHeld !== spaceHeld) set({ spaceHeld });
    },
    setDragOver(dragOver) {
      if (get().dragOver !== dragOver) set({ dragOver });
    },
    setPaint(paintSheetId, layerId) {
      const s = get();
      const paintLayerId = paintSheetId ? (layerId === undefined ? (paintSheetId === s.paintSheetId ? s.paintLayerId : null) : layerId) : null;
      if (s.paintSheetId === paintSheetId && s.paintLayerId === paintLayerId && (s.tool === 'sheet' || !paintSheetId)) return;
      set(paintSheetId ? { tool: 'sheet', creating: null, paintSheetId, paintLayerId } : { paintSheetId: null, paintLayerId: null });
    },
    setPaintLayer(paintLayerId) {
      if (get().paintLayerId !== paintLayerId) set({ paintLayerId });
    },
    setUiHidden(uiHidden) {
      if (get().uiHidden !== uiHidden) set({ uiHidden });
    },
    setDrawerOpen(drawerOpen) {
      if (get().drawerOpen !== drawerOpen) set(drawerOpen ? { drawerOpen } : { drawerOpen, drawerSaveRequested: false });
    },
    requestDrawerSave(v = true) {
      set(v ? { drawerOpen: true, uiHidden: false, drawerSaveRequested: true } : { drawerSaveRequested: false });
    },
    setFramesOpen(framesOpen) {
      if (get().framesOpen !== framesOpen) set({ framesOpen });
    },
    setCssEditorOpen(cssEditorOpen) {
      if (get().cssEditorOpen !== cssEditorOpen) set(cssEditorOpen ? { cssEditorOpen, uiHidden: false } : { cssEditorOpen, cssDraft: null });
    },
    setCssDraft(cssDraft) {
      if (get().cssDraft !== cssDraft) set({ cssDraft });
    },
  }));
}
